import * as React from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
// import Icon from "../Icon";
import Link from "next/link";
import Button from "@src/components/Button";
import { GetAlbumDetailsQuery } from "@src/@types/api.d";

type Props = {
  eventId: number;
  albumDetailsData: GetAlbumDetailsQuery;
  eventSlug: string;
};

export function insertAt(
  array: unknown[],
  index: number,
  ...elementsArray: unknown[]
) {
  array.splice(index, 0, ...elementsArray);
}

const Masonry: React.FC<Props> = ({ eventId, eventSlug, albumDetailsData }) => {
  const imageMedia =
    albumDetailsData?.getMedia?.filter((media) =>
      media?.type?.toLowerCase().includes("image")
    ) || [];
  const albumUrl = `https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`;

  return imageMedia.length > 0 ? (
    <div className="bg-white lg:pt-[5rem] pt-[2rem] sm:pt-[5rem] pb-[1rem] sm:pb-[3rem]">
      {/* Grid layout for screens smaller than lg */}
      <div className="block lg:hidden">
        <div
          className={`grid max-w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 md:gap-6 mb-12 mx-4 lg:mx-0`}
        >
          {imageMedia.slice(0, 4)?.map((image, index) => {
            if (image && image.meta?.s3_url) {
              return (
                <Link key={index} href={albumUrl} passHref>
                  <a target="_blank">
                    <div
                      className={`relative aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02]`}
                    >
                      <Image
                        placeholder="blur"
                        blurDataURL={`data:image/svg+xml;base64,${toBase64(
                          shimmer(700, 475)
                        )}`}
                        src={image.meta.s3_url}
                        layout="fill"
                        alt="gallery"
                        objectFit="cover"
                        className="rounded-2xl object-top"
                      />
                    </div>
                  </a>
                </Link>
              );
            }
          })}
        </div>
      </div>

      {/* Horizontal scrolling layout for lg screens and above */}
      <div className="hidden lg:block mb-12">
        <div className="relative overflow-hidden">
          <div
            className="flex gap-6 animate-scroll"
            style={{
              width: `${
                imageMedia.length * 320 + (imageMedia.length - 1) * 24
              }px`, // 320px width + 24px gap
            }}
          >
            {/* First set of images */}
            {imageMedia.map((image, index) => {
              if (image && image.meta?.s3_url) {
                return (
                  <Link key={`first-${index}`} href={albumUrl} passHref>
                    <a target="_blank">
                      <div className="relative w-80 aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02] flex-shrink-0">
                        <Image
                          placeholder="blur"
                          blurDataURL={`data:image/svg+xml;base64,${toBase64(
                            shimmer(700, 475)
                          )}`}
                          src={image.meta.s3_url}
                          layout="fill"
                          alt="gallery"
                          objectFit="cover"
                          className="rounded-2xl object-top"
                        />
                      </div>
                    </a>
                  </Link>
                );
              }
            })}
            {/* Duplicate set for seamless loop */}
            {imageMedia.map((image, index) => {
              if (image && image.meta?.s3_url) {
                return (
                  <Link key={`second-${index}`} href={albumUrl} passHref>
                    <a target="_blank">
                      <div className="relative w-80 aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02] flex-shrink-0">
                        <Image
                          placeholder="blur"
                          blurDataURL={`data:image/svg+xml;base64,${toBase64(
                            shimmer(700, 475)
                          )}`}
                          src={image.meta.s3_url}
                          layout="fill"
                          alt="gallery"
                          objectFit="cover"
                          className="rounded-2xl object-top"
                        />
                      </div>
                    </a>
                  </Link>
                );
              }
            })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${
              imageMedia.length * 320 + (imageMedia.length - 1) * 24
            }px);
          }
        }
        
        .animate-scroll {
          animation: scroll ${imageMedia.length * 4}s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  ) : (
    <></>
  );
};

export default Masonry;
